import React from 'react';
import { useAssistantWidget, AssistantPanelView } from '../../contexts/assistant-widget-context';
import { cn } from '../../utils/cn';

/**
 * Shared header chrome for the Fi and Peersona panels.
 *   - ``mode="chat"`` — clock + edit icon switcher and a caller-supplied
 *     avatar/name slot.
 *   - ``mode="list"`` — back arrow + centered list title.
 * The X close button shows in both modes; active-state highlights are
 * driven by ``assistantPanelView`` on ``AssistantWidgetContext``.
 */
interface AssistantPanelHeaderProps {
  actionsSlot?: React.ReactNode;
  avatarSlot?: React.ReactNode;
  listTitle?: string;
  mode: 'chat' | 'list';
  onClose: () => void;
}

const ClockIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 8v4l3 2M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BackArrowIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconButton: React.FC<{
  active: boolean;
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, ariaLabel, onClick, children }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    aria-pressed={active}
    title={ariaLabel}
    onClick={onClick}
    className={cn(
      'flex h-9 w-9 items-center justify-center rounded-lg border-0 p-0 transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300',
      active ? 'bg-gray-100 text-gray-900' : 'bg-transparent text-gray-600 hover:bg-gray-100',
    )}
  >
    {children}
  </button>
);

export const AssistantPanelHeader: React.FC<AssistantPanelHeaderProps> = ({
  actionsSlot,
  avatarSlot,
  listTitle,
  mode,
  onClose,
}) => {
  const { assistantPanelView, openChatView, openConversationsView, openNewConversationView } = useAssistantWidget();

  if (mode === 'list') {
    return (
      <header className="shrink-0 bg-white">
        <div className="flex items-center gap-1 px-1 py-1.5">
          <button
            type="button"
            aria-label="Back to conversation"
            onClick={openChatView}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-700 hover:bg-gray-100"
          >
            <BackArrowIcon />
          </button>
          <p className="font-albert-sans min-w-0 flex-1 truncate text-center text-sm font-semibold text-gray-900">
            {listTitle ?? ''}
          </p>
          <button
            type="button"
            aria-label="Close chat"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-600 hover:bg-gray-100"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="bg-input-border-gray h-px w-full" aria-hidden />
      </header>
    );
  }

  const isConversationsActive: boolean = (assistantPanelView as AssistantPanelView) === 'conversations';
  const isNewActive: boolean = (assistantPanelView as AssistantPanelView) === 'new';

  return (
    <header className="shrink-0 bg-white">
      <div className="flex items-center gap-2 px-1 py-1.5">
        <div className="flex shrink-0 items-center gap-1">
          <IconButton active={isConversationsActive} ariaLabel="Recent conversations" onClick={openConversationsView}>
            <ClockIcon />
          </IconButton>
          <IconButton active={isNewActive} ariaLabel="Start a new conversation" onClick={openNewConversationView}>
            <EditIcon />
          </IconButton>
        </div>
        <div className="bg-input-border-gray h-6 w-px shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">{avatarSlot}</div>
        {actionsSlot ? <div className="flex shrink-0 items-center gap-1">{actionsSlot}</div> : null}
        <button
          type="button"
          aria-label="Close chat"
          onClick={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-600 hover:bg-gray-100"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="bg-input-border-gray h-px w-full" aria-hidden />
    </header>
  );
};
